import { useMemo } from "react";
import { usePage } from "@inertiajs/react";
import { StepProgress } from "@components/ui/step-progress";
import { Deploy } from "@shared";
import { useDeployTasks } from "@api/deploys.hooks";
import { useRotatingQuote } from "@hooks/useRotatingQuote";
import { DeployPanel } from "@components/deploy/DeployPanel";

const CAMPAIGN_TASK_NAMES: string[] = [
  "ConnectingGoogle",
  "VerifyingGoogle",
  "CheckingBilling",
  "DeployingCampaign",
  "EnablingCampaign",
];

const fallbackSteps = [{ id: "preparing", label: "Preparing deployment" }];

export default function InProgressScreen({ actions }: { actions?: React.ReactNode }) {
  const { deploy } = usePage<{ deploy?: { id: number; status: string; tasks: any[] } }>().props;
  const { data } = useDeployTasks(deploy?.id, deploy?.tasks, deploy?.status);
  const tasks = data?.tasks;

  const { steps, currentStep, subtitle, hasCampaignTasks } = useMemo(() => {
    const allTasks = tasks ?? [];

    if (allTasks.length === 0) {
      return {
        steps: fallbackSteps,
        currentStep: 0,
        subtitle: undefined as string | undefined,
        hasCampaignTasks: false,
      };
    }

    const isTerminal = (status: string) =>
      status === "completed" || status === "skipped" || status === "passed";
    const completedCount = allTasks.filter((t) => isTerminal(t.status)).length;

    const running = allTasks.filter((t) => t.status === "running");
    let label: string | undefined;
    if (running.length === 1) {
      label = Deploy.TaskDescriptionMap[running[0].name as Deploy.TaskName] ?? running[0].name;
    } else if (running.length > 1) {
      const first =
        Deploy.TaskDescriptionMap[running[0].name as Deploy.TaskName] ?? running[0].name;
      label = `${first} + ${running.length - 1} other task${running.length - 1 > 1 ? "s" : ""}`;
    }

    return {
      steps: allTasks.map((t) => ({
        id: t.name,
        label: Deploy.TaskDescriptionMap[t.name as Deploy.TaskName] ?? t.name,
      })),
      currentStep: completedCount,
      subtitle: label,
      hasCampaignTasks: allTasks.some((t) => CAMPAIGN_TASK_NAMES.includes(t.name)),
    };
  }, [tasks]);

  const noun = hasCampaignTasks ? "website & campaign" : "website";
  const funQuote = useRotatingQuote("deploy");

  return (
    <DeployPanel.Root>
      {actions && (
        <DeployPanel.Header>
          <div />
          <DeployPanel.Actions>{actions}</DeployPanel.Actions>
        </DeployPanel.Header>
      )}
      <DeployPanel.Body centered className="p-12">
        <StepProgress
          title={`Launching your ${noun}`}
          subtitle={subtitle}
          steps={steps}
          currentStep={currentStep}
          funQuote={funQuote}
        />
      </DeployPanel.Body>
    </DeployPanel.Root>
  );
}
